/**
 * Central Firebase initialization
 * This file provides a single source of Firebase initialization for the entire application
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';

// Log environment variable status for debugging
console.log('[FIREBASE] Environment variables status:', {
  apiKey: import.meta.env.VITE_PRESENTATION_FIREBASE_API_KEY ? 'defined' : 'undefined',
  authDomain: import.meta.env.VITE_PRESENTATION_FIREBASE_AUTH_DOMAIN ? 'defined' : 'undefined',
  projectId: import.meta.env.VITE_PRESENTATION_FIREBASE_PROJECT_ID ? 'defined' : 'undefined'
});

// Stakeholder presentation Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCAaE1EFm1BHvLXB7UJ7rmjP1QXM70Ss4k",
  authDomain: "jimi-wellness-presentation.firebaseapp.com",
  projectId: "jimi-wellness-presentation",
  storageBucket: "jimi-wellness-presentation.firebasestorage.app",
  messagingSenderId: "856341610593",
  appId: "1:856341610593:web:5c8e989b4198a9f98eaa20",
  measurementId: "G-5Y26EGK0JX"
};

// Validate configuration before initializing
if (!firebaseConfig.apiKey) {
  console.error('[FIREBASE] Error: API key is missing or invalid');
}

// Initialize variables
let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let auth: Auth | null = null;

try {
  // Initialize Firebase
  console.log('[FIREBASE] Initializing with config:', {
    projectId: firebaseConfig.projectId,
    apiKeyLength: firebaseConfig.apiKey ? firebaseConfig.apiKey.length : 0,
    authDomain: firebaseConfig.authDomain
  });
  
  app = initializeApp(firebaseConfig, 'stakeholder-presentation');

  // Enable debug logging if debug flag is set
  if (import.meta.env.VITE_DEBUG_FIREBASE === 'true') {
    console.log('[FIREBASE] Debug mode enabled');
    console.log('[FIREBASE] Configuration:', {
      projectId: firebaseConfig.projectId,
      storageBucket: firebaseConfig.storageBucket,
      authDomain: firebaseConfig.authDomain
    });
  }

  // Initialize services
  db = getFirestore(app);
  storage = getStorage(app);
  auth = getAuth(app);
  
  // Check if we're in development mode and using localhost
  const isLocalDevelopment = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1';
  
  if (isLocalDevelopment) {
    console.log('[FIREBASE] Local development environment detected');
  }

  // Log initialization status
  console.log(`[FIREBASE] Services initialized for project: ${app.options.projectId}`);
  console.log(`[FIREBASE] Storage bucket: ${app.options.storageBucket}`);
} catch (error) {
  console.error('[FIREBASE] Initialization error:', error);
  app = null;
  db = null;
  storage = null;
  auth = null;
}

// Export the app and services
export const firebaseApp = app;
export const firestore = db;
export const storageRef = storage;
export const firebaseAuth = auth;

// Export with original names for backward compatibility
export { storage, db, auth, app };

// Export a function to get the current app
export function getFirebaseAppInstance() {
  if (!firebaseApp) {
    console.error('[FIREBASE] App instance requested but Firebase failed to initialize');
  }
  return firebaseApp;
}
